import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { parseCountry } from 'react-international-phone'
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap'
import { toast } from 'react-toastify'

import AuthPhoneInput from 'Components/auth/AuthPhoneInput'
import { AppDispatch, RootState, store } from 'store'
import { AuthPhoneValue } from 'types/auth'
import { getErrorMessage, isValidOTP } from 'utils/helpers'
import { emptyAuthPhoneForIso, getCountriesForIso, isValidAuthPhone, toInternationalDigits } from 'utils/phone'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { updateContactMobile } from 'services/api/creator-contacts'
import { generateRegistrationOTP } from 'services/api/auth'
import { resetCreatorsState } from 'slices/creators/reducer'
import './AddCreatorContactModal.scss'

const OTP_LENGTH = 6

const getMobileValidationError = (phone: AuthPhoneValue): string | undefined => {
    if (!phone.mobile.trim()) return 'Mobile number is required.'
    if (!isValidAuthPhone(phone)) return 'Please enter a valid mobile number.'
    return undefined
}

type Step = 'mobile' | 'otp'

const AddContactMobileModal: React.FC<{
    isOpen: boolean
    onClose: () => void
    socialAccountId?: string
    creatorName?: string
}> = ({ isOpen, onClose, socialAccountId, creatorName }) => {
    const config = useSelector((state: RootState) => state.auth.configurations)
    const mobileVerificationRequired = config?.['agency_creator_mobile_verification'] === 'y'
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId)
    const agencyIso = useSelector((state: RootState) => state.auth.selectedAgency?.country?.iso_code)

    const countries = useMemo(() => getCountriesForIso(agencyIso), [agencyIso])
    const defaultCountry = parseCountry(countries[0]).iso2
    const emptyPhone = useMemo(() => emptyAuthPhoneForIso(agencyIso), [agencyIso])

    const [processing, setProcessing] = useState(false)
    const [step, setStep] = useState<Step>('mobile')
    const [phone, setPhone] = useState('')
    const [phoneFields, setPhoneFields] = useState<AuthPhoneValue>(emptyPhone)
    const [mobileError, setMobileError] = useState<string | undefined>()
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''))
    const [resendIn, setResendIn] = useState(0)
    const [resending, setResending] = useState(false)
    const dispatch = useDispatch<AppDispatch>()

    const resetForm = () => {
        setStep('mobile')
        setPhone('')
        setPhoneFields(emptyAuthPhoneForIso(agencyIso))
        setMobileError(undefined)
        setOtp(Array(OTP_LENGTH).fill(''))
        setResendIn(0)
        setResending(false)
    }

    useEffect(() => {
        if (isOpen) resetForm()
    }, [isOpen, agencyIso])

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null
        if (resendIn > 0) {
            interval = setInterval(() => {
                setResendIn((prev) => prev - 1)
            }, 1000)
        }
        return () => {
            if (interval) clearInterval(interval)
        }
    }, [resendIn])

    const sendOTP = async () => {
        const error = getMobileValidationError(phoneFields)
        if (error) {
            setMobileError(error)
            return false
        }
        try {
            setProcessing(true)
            const success = await generateRegistrationOTP({
                mode: 'mobile',
                username: toInternationalDigits(phoneFields),
            })
            if (success) {
                setStep('otp')
                setResendIn(30)
                toast.success('OTP sent successfully.')
                return true
            }
            toast.error('Failed to send OTP. Please try again.')
            return false
        } catch (error: unknown) {
            toast.error(getErrorMessage(error) || 'Failed to send OTP. Please try again.')
            return false
        } finally {
            setProcessing(false)
        }
    }

    const handleResendOTP = async () => {
        if (resendIn || resending) return
        try {
            setResending(true)
            const success = await generateRegistrationOTP({
                mode: 'mobile',
                username: toInternationalDigits(phoneFields),
            })
            if (success) {
                setResendIn(30)
                toast.success('OTP has been resent!')
            }
        } catch (error: unknown) {
            toast.error(getErrorMessage(error) || 'Failed to resend OTP.')
        } finally {
            setResending(false)
        }
    }

    const handleSaveMobile = async (otpValue?: string) => {
        if (!selectedAgencyId || !socialAccountId) return

        const validationError = getMobileValidationError(phoneFields)
        if (validationError) {
            setMobileError(validationError)
            return
        }

        try {
            setProcessing(true)
            const payload = {
                mobile: phoneFields.mobile.trim(),
                country_code: phoneFields.country_code,
                ...(otpValue ? { otp: otpValue } : {}),
            }
            await updateContactMobile(selectedAgencyId, socialAccountId, payload)
            toast.success('Mobile number added successfully.')
            dispatch(resetCreatorsState())
            dispatch(fetchContactsThunk({ force: true, filters: store.getState().creators.filters }))
            onClose()
        } catch (error: unknown) {
            toast.error(getErrorMessage(error) || 'Failed to save mobile number. Please try again.')
        } finally {
            setProcessing(false)
        }
    }

    const handleVerifyAndSave = async (otpValue: string) => {
        await handleSaveMobile(otpValue)
    }

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value.replace(/\D/g, '').slice(-1)
        const newOtp = [...otp]
        newOtp[index] = value
        setOtp(newOtp)

        if (value && index < OTP_LENGTH - 1) {
            const nextInput = document.getElementById(`contact-mobile-otp-${index + 1}`) as HTMLInputElement
            nextInput?.focus()
        }

        if (isValidOTP(newOtp)) {
            handleVerifyAndSave(newOtp.join(''))
        }
    }

    const handleOtpBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const previousInput = document.getElementById(`contact-mobile-otp-${index - 1}`) as HTMLInputElement
            previousInput?.focus()
        }
    }

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = e.clipboardData.getData('Text').trim()
        if (pastedData.length === OTP_LENGTH && /^[0-9]+$/.test(pastedData)) {
            const newOtp = pastedData.split('')
            setOtp(newOtp)
            const lastInput = document.getElementById(`contact-mobile-otp-${OTP_LENGTH - 1}`) as HTMLInputElement
            lastInput?.focus()
            if (isValidOTP(newOtp)) {
                handleVerifyAndSave(newOtp.join(''))
            }
        }
        e.preventDefault()
    }

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className="add-contact-mobile-modal">
            <ModalHeader toggle={onClose}>
                Add Mobile
                {creatorName && (
                    <p className="modal-subtitle text-dark fw-medium mb-0 mt-1">{creatorName}</p>
                )}
            </ModalHeader>
            <ModalBody>
                {step === 'mobile' ? (
                    <>
                        <p className="modal-subtitle">
                            {mobileVerificationRequired
                                ? "Enter the creator's mobile number to receive an OTP for verification."
                                : "Enter the creator's mobile number."}
                        </p>
                        <label htmlFor="contact-mobile" className="form-label">
                            Mobile Number <span className="text-danger">*</span>
                        </label>
                        <AuthPhoneInput
                            value={phone}
                            defaultCountry={defaultCountry}
                            countries={countries}
                            hideDropdown
                            inputId="contact-mobile"
                            placeholder="Mobile Number"
                            invalid={Boolean(mobileError)}
                            onBlur={() => setMobileError(getMobileValidationError(phoneFields))}
                            onChange={(nextPhone, fields) => {
                                setPhone(nextPhone)
                                setPhoneFields(fields)
                                setMobileError(undefined)
                            }}
                        />
                        {mobileError && (
                            <div className="text-danger field-error">{mobileError}</div>
                        )}
                    </>
                ) : (
                    <>
                        <p className="modal-subtitle">
                            Enter the 6-digit OTP sent to{' '}
                            <span className="fw-semibold">
                                +{phoneFields.country_code} {phoneFields.mobile}
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ms-1"
                                    onClick={() => {
                                        setStep('mobile')
                                        setOtp(Array(OTP_LENGTH).fill(''))
                                    }}
                                    aria-label="Change mobile number"
                                >
                                    <i className="ri-pencil-line" />
                                </button>
                            </span>
                        </p>
                        <Row className="g-2 justify-content-center">
                            {otp.map((digit, index) => (
                                <Col key={index} xs="2">
                                    <input
                                        className="form-control text-center add-contact-mobile-otp-input"
                                        autoComplete="off"
                                        id={`contact-mobile-otp-${index}`}
                                        type="tel"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(e, index)}
                                        onKeyDown={(e) => handleOtpBackspace(e, index)}
                                        onPaste={index === 0 ? handleOtpPaste : undefined}
                                    />
                                </Col>
                            ))}
                        </Row>
                        <p className="text-center text-muted fs-13 mb-0 mt-3">
                            Didn&apos;t receive a code?{' '}
                            <button
                                type="button"
                                className={`btn btn-link p-0 ${resendIn || resending ? 'text-secondary' : ''}`}
                                disabled={!!resendIn || resending}
                                onClick={handleResendOTP}
                            >
                                {resendIn ? `Resend in ${resendIn}s` : resending ? 'Sending...' : 'Resend OTP'}
                            </button>
                        </p>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <button type="button" className="theme-btn btn-primary-outlined" onClick={onClose}>
                    Cancel
                </button>
                {step === 'mobile' ? (
                    <button
                        type="button"
                        className="theme-btn btn-primary"
                        onClick={mobileVerificationRequired ? sendOTP : () => handleSaveMobile()}
                        disabled={processing || !phoneFields.mobile.trim()}
                    >
                        {processing
                            ? mobileVerificationRequired
                                ? 'Sending...'
                                : 'Saving...'
                            : mobileVerificationRequired
                              ? 'Send OTP'
                              : 'Save'}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="theme-btn btn-primary"
                        onClick={() => {
                            if (isValidOTP(otp)) handleVerifyAndSave(otp.join(''))
                        }}
                        disabled={processing || !isValidOTP(otp)}
                    >
                        {processing ? 'Verifying...' : 'Verify & Save'}
                    </button>
                )}
            </ModalFooter>
        </Modal>
    )
}

export default AddContactMobileModal
